import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { categories, stats, works } from "@/mocks/works";

export default function Hero() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const featured = works[0];

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/works?q=${encodeURIComponent(q)}` : "/works");
  };

  return (
    <section className="relative overflow-hidden bg-background-100 border-b border-background-200">
      <span
        aria-hidden="true"
        className="hidden xl:block absolute right-[-3rem] top-1/2 -translate-y-1/2 font-heading text-[22rem] leading-none font-bold text-primary-100/70 select-none pointer-events-none"
      >
        爆
      </span>

      <div className="relative px-4 md:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 animate-rise">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-700 bg-primary-100 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
              本期征稿进行中 · 投稿截止 10 月 20 日
            </span>

            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-[1.25] tracking-tight">
              把身边的爆料写下来，
              <br className="hidden sm:block" />
              让每个细节都被看见
            </h1>

            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              这里汇聚爆料大赛最新参赛作品与投稿作品，从热门参赛到优秀作品集一站速览。想看大家爆了哪些猛料、哪条投稿最有看头，直接进作品区翻。
            </p>

            <form onSubmit={handleSearch} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl" role="search">
              <label className="flex-1 flex items-center gap-2.5 bg-background-50 border border-background-300 rounded-md px-4 h-12 focus-within:border-primary-400 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索作品标题、主题或关键词"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                  aria-label="搜索参赛作品"
                />
              </label>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 h-12 rounded-md whitespace-nowrap transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-2-line" />
                </span>
                搜索作品
              </button>
            </form>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-500">热门方向：</span>
              {categories.slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/works?category=${c.id}`}
                  className="text-xs text-secondary-900 bg-secondary-100 hover:bg-secondary-200 px-3 py-1.5 rounded-full transition-colors cursor-pointer"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 animate-rise" style={{ animationDelay: "0.15s" }}>
            <Link
              to={`/work/${featured.id}`}
              className="group block rounded-lg overflow-hidden border border-background-200 bg-background-50 cursor-pointer"
              aria-label={`阅读本期头号爆料：${featured.title}`}
            >
              <div className="relative w-full h-[260px] md:h-[300px] bg-background-200">
                <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-xs text-primary-950 bg-accent-400 px-3 py-1.5 rounded-full">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-fire-line" />
                  </span>
                  本期头号爆料
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-xs text-background-100/90">{featured.categoryName}</p>
                  <h2 className="mt-1.5 font-heading text-lg md:text-xl font-bold text-background-50 leading-snug">
                    {featured.title}
                  </h2>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{featured.summary}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-3-line" />
                    </span>
                    {featured.author}
                  </span>
                  <span className="flex items-center gap-1.5 text-primary-600 font-medium">
                    阅读全文
                    <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>

        <dl
          className="mt-12 pt-8 border-t border-background-300 grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4 animate-rise"
          style={{ animationDelay: "0.3s" }}
        >
          {stats.map((s) => (
            <div key={s.label} className="lg:border-l lg:first:border-l-0 lg:border-background-300 lg:pl-6 lg:first:pl-0">
              <dt className="text-xs text-foreground-500">{s.label}</dt>
              <dd className="mt-2 flex items-baseline gap-1">
                <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{s.value}</span>
                <span className="text-sm text-foreground-500">{s.unit}</span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}